import { HTMLAttributes } from 'react'
import { useRouter } from 'next/router'

import Container from '@/components/base/Container'
import Cta from '@/components/base/Cta'
import Grid from '@/components/base/Grid'
import Image from '@/components/base/Image'
import Motion from '@/components/base/Motion'
import Typography from '@/components/base/Typography'

import { textOnImage } from './animations'
import { cn } from '@/lib/utils'
import { WPImage, WPLink, WPPageData } from '@/types'

export type TextOnImageProps = HTMLAttributes<HTMLElement> &
  Partial<{
    image: WPImage
    pretitle: string
    title: string
    text: string
    link: WPLink
    theme: WPPageData['acf']['theme']
    alignment: 'left' | 'right'
    is_boxed: boolean
    use_square_layout: boolean
    is_in_lightbox: boolean
  }>

const TextOnImage = ({
  image,
  pretitle,
  title,
  text,
  link,
  alignment,
  theme,
  is_boxed = false,
  is_in_lightbox = true,
  use_square_layout,
  ...props
}: TextOnImageProps) => {
  const router = useRouter()

  const handleSectionClick = () => {
    if (link?.url) router.push(link.url, link.url, { scroll: false })
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (link?.url && e.key === 'Enter')
      router.push(link.url, link.url, { scroll: false })
  }

  return (
    <Motion animation={textOnImage}>
      {(motionRef) => (
        <section
          ref={motionRef}
          {...props}
          className={cn([
            'relative',
            {
              'h-svh': !use_square_layout,
              'bg-white': theme === 'light',
              'bg-dark-600': theme === 'dark',
              'cursor-pointer': link?.url
            }
          ])}
          data-gallery-wrapper={is_in_lightbox}
          onClick={link?.url ? handleSectionClick : undefined}
          onKeyDown={link?.url ? handleKeyDown : undefined}
          role={link?.url ? 'link' : undefined}
          tabIndex={link?.url ? 0 : undefined}
        >
          {is_boxed ? (
            <Container className="absolute left-0 top-0 z-10 h-full w-full">
              <div
                className={cn([
                  'relative h-full w-full',
                  'after:absolute after:left-0 after:top-0 after:h-full after:w-full after:bg-black/60',
                  { 'aspect-h-1 aspect-w-1': use_square_layout }
                ])}
                data-motion-image
              >
                {image?.url ? (
                  <Image
                    source={image}
                    fill
                    fit="cover"
                    className="h-full w-full"
                    data-gallery={is_in_lightbox}
                  />
                ) : null}
              </div>
            </Container>
          ) : (
            <div
              className={cn([
                'relative z-10 h-full w-full',
                'after:absolute after:left-0 after:top-0 after:h-full after:w-full after:bg-black/50',
                { 'aspect-h-1 aspect-w-1': use_square_layout }
              ])}
            >
              <Image
                source={image}
                fill
                fit="cover"
                className="h-full w-full"
                data-motion-image
                data-gallery={is_in_lightbox}
              />
            </div>
          )}

          <Container className="absolute left-0 top-0 z-10 h-full w-full text-white">
            <Grid className={cn(['h-full', { 'px-gutter md:px-0': is_boxed }])}>
              <div
                className={cn(
                  'gap-[3.2rem]sm:col-span-4 col-span-4 col-start-1 flex h-full flex-col justify-center gap-[3.2rem] sm:col-start-2 lg:col-span-6 lg:col-start-2 xl:col-span-4 xl:col-start-2',
                  alignment === 'right' &&
                    'md:col-start-2 lg:col-start-7 xl:col-start-8'
                )}
              >
                {pretitle ? (
                  <Typography
                    variant="label"
                    className="font-extrabold"
                    data-motion-pretitle
                  >
                    {pretitle}
                  </Typography>
                ) : null}
                {title ? (
                  <Typography variant="h2" data-motion-title>
                    {title}
                  </Typography>
                ) : null}
                {text ? (
                  <Typography variant="body" data-motion-paragraph>
                    {text}
                  </Typography>
                ) : null}
                {link?.url ? (
                  <Cta
                    link={link}
                    theme="light"
                    className="!text-white"
                    data-motion-cta
                  />
                ) : null}
              </div>
            </Grid>
          </Container>
        </section>
      )}
    </Motion>
  )
}

export default TextOnImage
